/**
 * Video Scripts Panel
 *
 * Shows all slide scripts for a video outline with:
 * - "Generate All Scripts" functionality
 * - Video-level audio settings (voice, model)
 * - "Generate All Audio" batch generation
 * - Per-slide audio status
 */

import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { VoiceSelector } from '@/components/slides/VoiceSelector';
import {
  Loader2,
  FileText,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Send,
  Play,
  CheckCircle2,
  XCircle,
  Pencil,
  Check,
  X,
  List,
  AlignJustify,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { MiniAudioPlayer } from './MiniAudioPlayer';
import { Input } from '@/components/ui/input';
import { outlineAPI } from '@/services/api';
import {
  getAudioSettings,
  updateAudioSettings,
  generateAudio,
  getConversationAudioStatus,
  getAvailableVoices,
  applyAudioWithChoreography,
  updateScript,
  type SlideAudioStatusSummary,
  type TTSVoice,
} from '@/services/audioService';
import { useApplyJobStatus } from '@/hooks/useApplyJobStatus';
import {
  subscribeToAgentJob,
  getAgentJobStatus,
  getActiveAgentJobs,
  cancelAgentJob,
  type AgentJobProgress,
} from '@/services/agentJobService';
import { toast } from 'sonner';
import type { VideoOutline, SlideOutline } from '@/types/outline';
import { AIThinkingCard } from '@/components/common/AIThinkingCard';

interface SlideScript {
  slideId: string;          // Database UUID - used for API calls
  slideIdentifier: string;  // "slide-001" etc - used for display/sorting
  childConversationId: string;
  title: string;
  script: {
    scriptText: string | null;
    promptFlash: string | null;
    promptPro: string | null;
    preferredModel: string | null;
    audioUrl: string | null;
    audioDuration: number | null;
    updatedAt: string | null;
  } | null;
  hasAudio: boolean;
}

interface VideoScriptsPanelProps {
  conversationId: string;
  outline: VideoOutline;
  onScriptsGenerated?: () => void;
  className?: string;
}

// Script Card Component - Full script display with audio status, inline editing, and audio playback
const ScriptCard: React.FC<{
  slide: SlideOutline;
  script: SlideScript | undefined;
  slideIndex: number;
  audioStatus?: SlideAudioStatusSummary;
  isForceGenerating?: boolean;  // Persistent flag to prevent flickering
  isExpanded: boolean;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (text: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  isSaving: boolean;
  currentlyPlayingSlideId: string | null;
  onPlayChange: (slideId: string, playing: boolean) => void;
}> = ({
  slide,
  script,
  slideIndex,
  audioStatus,
  isForceGenerating,
  isExpanded,
  isEditing,
  editText,
  onEditTextChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  isSaving,
  currentlyPlayingSlideId,
  onPlayChange,
}) => {
  const hasScript = !!script?.script?.scriptText;
  const scriptText = script?.script?.scriptText || '';
  const wordCount = scriptText ? scriptText.split(/\s+/).filter(Boolean).length : 0;
  const estimatedDuration = Math.round(wordCount / 2.17); // ~130 WPM

  // Use combined visibility: persistent flag OR server status
  // This prevents flickering when SSE events are in transit
  const isGenerating = isForceGenerating ||
                       audioStatus?.audioStatus === 'GENERATING' ||
                       audioStatus?.audioStatus === 'ALIGNING';
  const isCompleted = !isForceGenerating && audioStatus?.audioStatus === 'COMPLETED';
  const isFailed = !isForceGenerating && audioStatus?.audioStatus === 'FAILED';
  const isPending = !isForceGenerating && (audioStatus?.audioStatus === 'PENDING' || !audioStatus?.audioStatus);

  // Audio playback state
  const slideId = script?.slideId;
  const isThisPlaying = slideId ? currentlyPlayingSlideId === slideId : false;

  const handlePlayChange = (playing: boolean) => {
    if (slideId) {
      onPlayChange(slideId, playing);
    }
  };

  // Handle keyboard shortcuts for editing
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      onCancelEdit();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      onSaveEdit();
    }
  };

  return (
    <div className="bg-card/50 rounded-lg border border-border/50 overflow-hidden">
      {/* Compact Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border/30">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-shrink-0 w-5 h-5 rounded bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">
            {slideIndex + 1}
          </span>
          <span className="text-xs font-medium truncate">
            {slide.title || `Slide ${slideIndex + 1}`}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {hasScript && (
            <span className="text-[10px] text-muted-foreground">
              {wordCount}w · {estimatedDuration}s
            </span>
          )}
        </div>
      </div>

      {/* Audio Player Row - Show in expanded mode when audio is completed */}
      {isExpanded && isCompleted && slideId && (
        <div className="px-3 py-2 bg-muted/10 border-b border-border/20">
          <MiniAudioPlayer
            slideId={slideId}
            audioDuration={audioStatus?.audioDuration}
            isPlaying={isThisPlaying}
            onPlayChange={handlePlayChange}
          />
        </div>
      )}

      {/* Audio Status Row - Shows inline progress (compact mode or when not completed) */}
      {(!isExpanded || !isCompleted) && (
        <div className="px-3 py-1.5 bg-muted/10 border-b border-border/20">
          {isCompleted && audioStatus?.audioDuration && (
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              <span className="text-[10px] text-green-700 font-medium">Audio Ready</span>
              <span className="text-[10px] text-muted-foreground ml-auto">
                {Math.floor((audioStatus.audioDuration || 0) / 60)}:{String(Math.floor((audioStatus.audioDuration || 0) % 60)).padStart(2, '0')}
              </span>
            </div>
          )}
          {isCompleted && !audioStatus?.audioDuration && (
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              <span className="text-[10px] text-green-700 font-medium">Audio Ready</span>
            </div>
          )}
          {isGenerating && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 text-amber-600 animate-spin" />
                <span className="text-[10px] text-amber-700 font-medium">
                  {audioStatus?.audioStatus === 'ALIGNING' ? 'Aligning...' : 'Generating audio...'}
                </span>
              </div>
              <div className="h-1 bg-amber-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full animate-pulse" style={{ width: '60%' }} />
              </div>
            </div>
          )}
          {isFailed && (
            <div className="flex items-center gap-1.5">
              <XCircle className="w-3 h-3 text-red-600" />
              <span className="text-[10px] text-red-700 font-medium">Failed</span>
            </div>
          )}
          {isPending && hasScript && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full border border-muted-foreground/30" />
              <span className="text-[10px] text-muted-foreground">Pending</span>
            </div>
          )}
        </div>
      )}

      {/* Script Text / Edit Mode */}
      <div className="p-3">
        {isEditing ? (
          // Edit mode
          <div className="space-y-2">
            <Textarea
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[100px] text-[11px] leading-relaxed resize-none"
              placeholder="Enter script text..."
              autoFocus
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">
                Ctrl+Enter to save, Escape to cancel
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={onCancelEdit}
                  disabled={isSaving}
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={onSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3 mr-1" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          </div>
        ) : hasScript ? (
          // Display mode
          <div>
            <p className={cn(
              "text-[11px] text-foreground/90 leading-relaxed whitespace-pre-wrap",
              !isExpanded && "line-clamp-3"
            )}>
              {scriptText}
            </p>
            {/* Edit button in expanded mode */}
            {isExpanded && (
              <div className="flex justify-end mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground"
                  onClick={onStartEdit}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground italic py-2 text-center">
            No script yet — click "Generate All" above
          </p>
        )}
      </div>
    </div>
  );
};

export const VideoScriptsPanel: React.FC<VideoScriptsPanelProps> = ({
  conversationId,
  outline,
  onScriptsGenerated,
  className,
}) => {
  const [scripts, setScripts] = useState<SlideScript[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<AgentJobProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Script generation job persistence state
  const [scriptJobId, setScriptJobId] = useState<string | null>(null);
  const scriptUnsubscribeRef = useRef<(() => void) | null>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editInput, setEditInput] = useState('');
  const [editProgress, setEditProgress] = useState<AgentJobProgress | null>(null);

  // Edit job persistence state
  const [editJobId, setEditJobId] = useState<string | null>(null);
  const editUnsubscribeRef = useRef<(() => void) | null>(null);

  // Audio settings state
  const [voices, setVoices] = useState<TTSVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('Kore');
  const [selectedModel, setSelectedModel] = useState<'flash' | 'pro'>('flash');
  const [audioStatus, setAudioStatus] = useState<SlideAudioStatusSummary[]>([]);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const audioPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Track which slides are actively generating (to prevent flickering during status transitions)
  const [generatingSlideIds, setGeneratingSlideIds] = useState<Set<string>>(new Set());

  // View mode: compact (truncated scripts) or expanded (full scripts with audio player)
  const [viewMode, setViewMode] = useState<'compact' | 'expanded'>('compact');

  // Inline editing state
  const [editingSlideId, setEditingSlideId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Audio playback state - only one slide can play at a time
  const [currentlyPlayingSlideId, setCurrentlyPlayingSlideId] = useState<string | null>(null);

  // Video-level script generation status
  // hasNarrativeContext = true means video_script_planner has been run with unified voice prompts
  const [hasNarrativeContext, setHasNarrativeContext] = useState<boolean>(false);

  // Apply job status hook for tracking animation choreography jobs
  const {
    registerJob: registerApplyJob,
    startApplyAll,
    endApplyAll,
  } = useApplyJobStatus(conversationId);

  // Load existing scripts
  const loadScripts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await outlineAPI.getScripts(conversationId);
      setScripts(response.scripts);
      // Track if video-level script generation has been done
      setHasNarrativeContext(response.hasNarrativeContext ?? false);
    } catch (err) {
      console.error('Failed to load scripts:', err);
      setError('Failed to load scripts');
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    loadScripts();
  }, [loadScripts, outline.slides]);

  // Load voices and audio settings
  const loadVoicesAndSettings = useCallback(async () => {
    try {
      const [voicesResult, settingsResult] = await Promise.all([
        getAvailableVoices(),
        getAudioSettings(conversationId).catch(() => null),
      ]);
      setVoices(voicesResult.voices);
      if (settingsResult) {
        setSelectedVoice(settingsResult.voiceId || 'Kore');
        // Map ttsModel to flash/pro
        setSelectedModel(settingsResult.ttsModel?.includes('pro') ? 'pro' : 'flash');
      }
    } catch (err) {
      console.error('Failed to load voices/settings:', err);
    }
  }, [conversationId]);

  // Load audio status for all slides
  const loadAudioStatus = useCallback(async () => {
    try {
      const status = await getConversationAudioStatus(conversationId);
      setAudioStatus(status.slides);

      // Check if any slides are still generating
      const anyGenerating = status.slides.some(
        s => s.audioStatus === 'GENERATING' || s.audioStatus === 'ALIGNING'
      );
      if (!anyGenerating && isGeneratingAudio) {
        setIsGeneratingAudio(false);
        if (audioPollingRef.current) {
          clearInterval(audioPollingRef.current);
          audioPollingRef.current = null;
        }
        toast.success('Audio generation complete!');
      }
    } catch (err) {
      console.error('Failed to load audio status:', err);
    }
  }, [conversationId, isGeneratingAudio]);

  // Pre-initialize audioStatus from outline to prevent race condition
  // This ensures SSE events have entries to match against before API call completes
  useEffect(() => {
    if (outline?.slides?.length && audioStatus.length === 0) {
      // Filter out deleted slides
      const activeSlides = outline.slides.filter(s => !s.isDeleted);
      const initialStatus: SlideAudioStatusSummary[] = activeSlides.map((slide, index) => ({
        slideId: '', // Will be populated by loadAudioStatus
        slideIdentifier: slide.id, // e.g., "slide-001"
        slideNumber: index + 1,
        title: slide.title,
        hasAudio: false,
        audioStatus: 'PENDING' as const,
        audioUrl: null,
        audioDuration: null,
        version: null,
        scriptText: null,
      }));
      console.log('🎧 [VideoScriptsPanel] Pre-initializing audioStatus with', initialStatus.length, 'slides from outline');
      setAudioStatus(initialStatus);
    }
  }, [outline?.slides]);

  // Load voices, settings, and audio status on mount
  useEffect(() => {
    loadVoicesAndSettings();
    loadAudioStatus();
  }, [loadVoicesAndSettings, loadAudioStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (audioPollingRef.current) {
        clearInterval(audioPollingRef.current);
      }
    };
  }, []);

  // Listen for real-time audio events (SSE dispatched as window events)
  useEffect(() => {
    const handleAudioEvent = (event: CustomEvent) => {
      // Event structure: { conversationId, audioEvent: { type, slideId, slideIdentifier, outlineSlideId, status, ... }, timestamp }
      const audioEvent = event.detail?.audioEvent || {};
      const { type, slideId, slideIdentifier, outlineSlideId, status, audioUrl, audioDuration } = audioEvent;

      console.log('🎧 [VideoScriptsPanel] Audio event:', { type, slideId, slideIdentifier, outlineSlideId, status });

      // Update audio status for the specific slide
      // For video conversations, outlineSlideId matches our pre-initialized slideIdentifier (e.g., "slide-002")
      // For non-video, fall back to slideIdentifier or slideId matching
      if (slideId || slideIdentifier || outlineSlideId) {
        setAudioStatus(prev => {
          // Priority: slideId (most reliable) > outlineSlideId > slideIdentifier
          // slideId is the database UUID which is unique per slide
          // outlineSlideId is the parent outline's slide ID (e.g., "slide-002")
          // slideIdentifier is always "slide-001" for child conversation slides - least reliable!
          const idx = prev.findIndex(s =>
            (slideId && s.slideId === slideId) ||  // Most reliable: match by database UUID
            (outlineSlideId && s.slideIdentifier === outlineSlideId) ||  // Video: match outline slide ID
            (slideId && s.slideIdentifier === slideId)  // Fallback: slideId as identifier
            // Note: removed slideIdentifier matching as it's always "slide-001" for all child slides
          );
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              slideId: slideId || updated[idx].slideId, // Update slideId if we now have it
              audioStatus: status || updated[idx].audioStatus,
              hasAudio: status === 'COMPLETED' || !!audioUrl || updated[idx].hasAudio,
              audioUrl: audioUrl || updated[idx].audioUrl,
              audioDuration: audioDuration ?? updated[idx].audioDuration,
            };
            console.log('🎧 [VideoScriptsPanel] ✅ Updated slide:', updated[idx].slideIdentifier, '→', status);
            return updated;
          }
          console.log('🎧 [VideoScriptsPanel] ⚠️ Slide not found:', { slideId, slideIdentifier, outlineSlideId }, 'Array has:', prev.map(s => s.slideIdentifier));
          return prev;
        });
      }

      // Handle completion or failure - remove from generating set
      if (status === 'COMPLETED' || status === 'FAILED' || type === 'audio_generated' || type === 'audio_error') {
        // Remove this slide from the generating set
        setGeneratingSlideIds(prev => {
          const next = new Set(prev);
          if (slideId) next.delete(slideId);
          console.log('🎧 [VideoScriptsPanel] Removed from generating set:', slideId, 'remaining:', Array.from(next));
          return next;
        });

        // Reload full status to verify all slides
        if (type === 'audio_generated' || status === 'COMPLETED') {
          loadAudioStatus();
        }
      }
    };

    window.addEventListener('audio-event', handleAudioEvent as EventListener);
    return () => window.removeEventListener('audio-event', handleAudioEvent as EventListener);
  }, [loadAudioStatus]);

  // Listen for script-updated events from ScriptAudioPanel (cross-panel sync)
  useEffect(() => {
    const handleScriptUpdated = (event: CustomEvent<{
      conversationId: string;
      slideId: string;
      scriptText: string;
      timestamp: string;
    }>) => {
      const { slideId, scriptText } = event.detail;

      // Update local state if this slide is in our list
      setScripts(prev => {
        const idx = prev.findIndex(s => s.slideId === slideId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            script: {
              ...updated[idx].script!,
              scriptText,
              updatedAt: new Date().toISOString(),
            },
          };
          console.log('[VideoScriptsPanel] Script updated via cross-panel sync:', slideId);
          return updated;
        }
        return prev;
      });
    };

    window.addEventListener('script-updated', handleScriptUpdated as EventListener);
    return () => window.removeEventListener('script-updated', handleScriptUpdated as EventListener);
  }, []);

  // Listen for apply-all-completed event from useApplyJobStatus hook
  // This fires when all choreography jobs complete, allowing us to reset local isApplyingAll state
  useEffect(() => {
    const handleApplyAllCompleted = (event: Event) => {
      const customEvent = event as CustomEvent<{ conversationId: string }>;
      if (customEvent.detail?.conversationId === conversationId) {
        console.log('[VideoScriptsPanel] Apply All completed via hook event');
        setIsApplyingAll(false);
        toast.success('Audio applied to all slides with animations synced!');
        loadAudioStatus();
      }
    };

    window.addEventListener('apply-all-completed', handleApplyAllCompleted);
    return () => window.removeEventListener('apply-all-completed', handleApplyAllCompleted);
  }, [conversationId, loadAudioStatus]);

  // Reconnect to active script generation/edit jobs on mount or conversationId change
  useEffect(() => {
    const reconnectToActiveJobs = async () => {
      // Skip if already generating or editing
      if (isGenerating || isEditing) return;

      // 1. Check sessionStorage for saved script generation job
      const savedScriptJobId = sessionStorage.getItem(`script_generation_${conversationId}`);
      if (savedScriptJobId) {
        try {
          const { job } = await getAgentJobStatus(savedScriptJobId);
          if (job.status === 'PENDING' || job.status === 'PROCESSING') {
            console.log('[VideoScriptsPanel] Reconnecting to script generation job:', savedScriptJobId);
            setScriptJobId(savedScriptJobId);
            setIsGenerating(true);
            setGenerationProgress(job.progress);
            const unsubscribe = subscribeToAgentJob(savedScriptJobId, {
              onProgress: (progress) => setGenerationProgress(progress),
              onComplete: async () => {
                // Close SSE connection to prevent auto-reconnect
                scriptUnsubscribeRef.current?.();
                scriptUnsubscribeRef.current = null;
                sessionStorage.removeItem(`script_generation_${conversationId}`);
                setScriptJobId(null);
                setIsGenerating(false);
                setGenerationProgress(null);
                await loadScripts();
                // Notify ScriptAudioPanel to reload from DB
                window.dispatchEvent(new CustomEvent('scripts-batch-updated', {
                  detail: { conversationId, timestamp: Date.now(), source: 'video_script_planner' }
                }));
                onScriptsGenerated?.();
              },
              onError: (errorMsg) => {
                // Close SSE connection on error
                scriptUnsubscribeRef.current?.();
                scriptUnsubscribeRef.current = null;
                sessionStorage.removeItem(`script_generation_${conversationId}`);
                setScriptJobId(null);
                setError(errorMsg);
                setIsGenerating(false);
                setGenerationProgress(null);
              },
            });
            scriptUnsubscribeRef.current = unsubscribe;
            return; // Found active generation job, skip edit check
          } else {
            sessionStorage.removeItem(`script_generation_${conversationId}`);
          }
        } catch (e) {
          console.log('[VideoScriptsPanel] Saved generation job not found, clearing');
          sessionStorage.removeItem(`script_generation_${conversationId}`);
        }
      }

      // 2. Check sessionStorage for saved script edit job
      const savedEditJobId = sessionStorage.getItem(`script_edit_${conversationId}`);
      if (savedEditJobId) {
        try {
          const { job } = await getAgentJobStatus(savedEditJobId);
          if (job.status === 'PENDING' || job.status === 'PROCESSING') {
            console.log('[VideoScriptsPanel] Reconnecting to script edit job:', savedEditJobId);
            setEditJobId(savedEditJobId);
            setIsEditing(true);
            setEditProgress(job.progress);
            const unsubscribe = subscribeToAgentJob(savedEditJobId, {
              onProgress: (progress) => setEditProgress(progress),
              onComplete: async () => {
                // Close SSE connection to prevent auto-reconnect
                editUnsubscribeRef.current?.();
                editUnsubscribeRef.current = null;
                sessionStorage.removeItem(`script_edit_${conversationId}`);
                setEditJobId(null);
                setIsEditing(false);
                setEditProgress(null);
                await loadScripts();
                // Notify ScriptAudioPanel to reload from DB
                window.dispatchEvent(new CustomEvent('scripts-batch-updated', {
                  detail: { conversationId, timestamp: Date.now(), source: 'video_script_planner' }
                }));
                onScriptsGenerated?.();
              },
              onError: (errorMsg) => {
                // Close SSE connection on error
                editUnsubscribeRef.current?.();
                editUnsubscribeRef.current = null;
                sessionStorage.removeItem(`script_edit_${conversationId}`);
                setEditJobId(null);
                setError(errorMsg);
                setIsEditing(false);
                setEditProgress(null);
              },
            });
            editUnsubscribeRef.current = unsubscribe;
            return;
          } else {
            sessionStorage.removeItem(`script_edit_${conversationId}`);
          }
        } catch (e) {
          console.log('[VideoScriptsPanel] Saved edit job not found, clearing');
          sessionStorage.removeItem(`script_edit_${conversationId}`);
        }
      }

      // 3. Fallback: Query active jobs for this conversation
      try {
        const activeJobs = await getActiveAgentJobs(conversationId);
        const scriptJob = activeJobs.jobs.find(j => j.agentName === 'video_script_planner');
        if (scriptJob && (scriptJob.status === 'PENDING' || scriptJob.status === 'PROCESSING')) {
          console.log('[VideoScriptsPanel] Found active job via API:', scriptJob.id);
          sessionStorage.setItem(`script_generation_${conversationId}`, scriptJob.id);
          setScriptJobId(scriptJob.id);
          setIsGenerating(true);
          setGenerationProgress(scriptJob.progress);
          const unsubscribe = subscribeToAgentJob(scriptJob.id, {
            onProgress: (progress) => setGenerationProgress(progress),
            onComplete: async () => {
              // Close SSE connection to prevent auto-reconnect
              scriptUnsubscribeRef.current?.();
              scriptUnsubscribeRef.current = null;
              sessionStorage.removeItem(`script_generation_${conversationId}`);
              setScriptJobId(null);
              setIsGenerating(false);
              setGenerationProgress(null);
              await loadScripts();
              // Notify ScriptAudioPanel to reload from DB
              window.dispatchEvent(new CustomEvent('scripts-batch-updated', {
                detail: { conversationId, timestamp: Date.now(), source: 'video_script_planner' }
              }));
              onScriptsGenerated?.();
            },
            onError: (errorMsg) => {
              // Close SSE connection on error
              scriptUnsubscribeRef.current?.();
              scriptUnsubscribeRef.current = null;
              sessionStorage.removeItem(`script_generation_${conversationId}`);
              setScriptJobId(null);
              setError(errorMsg);
              setIsGenerating(false);
              setGenerationProgress(null);
            },
          });
          scriptUnsubscribeRef.current = unsubscribe;
        }
      } catch (e) {
        console.log('[VideoScriptsPanel] Could not query active jobs:', e);
      }
    };

    reconnectToActiveJobs();
  }, [conversationId, isGenerating, isEditing, loadScripts, onScriptsGenerated]);

  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      if (scriptUnsubscribeRef.current) {
        scriptUnsubscribeRef.current();
      }
      if (editUnsubscribeRef.current) {
        editUnsubscribeRef.current();
      }
    };
  }, []);

  // Cancel handlers
  const handleCancelGeneration = async () => {
    if (!scriptJobId) return;
    try {
      await cancelAgentJob(scriptJobId);
      sessionStorage.removeItem(`script_generation_${conversationId}`);
      setScriptJobId(null);
      setIsGenerating(false);
      setGenerationProgress(null);
      scriptUnsubscribeRef.current?.();
      scriptUnsubscribeRef.current = null;
    } catch (err) {
      console.error('[VideoScriptsPanel] Failed to cancel generation:', err);
    }
  };

  const handleCancelEdit = async () => {
    if (!editJobId) return;
    try {
      await cancelAgentJob(editJobId);
      sessionStorage.removeItem(`script_edit_${conversationId}`);
      setEditJobId(null);
      setIsEditing(false);
      setEditProgress(null);
      editUnsubscribeRef.current?.();
      editUnsubscribeRef.current = null;
    } catch (err) {
      console.error('[VideoScriptsPanel] Failed to cancel edit:', err);
    }
  };

  // Handle voice change - also save voiceName for syncing across panels
  const handleVoiceChange = async (voiceId: string) => {
    setSelectedVoice(voiceId);
    const voiceName = voices.find(v => v.id === voiceId)?.name || voiceId;
    try {
      await updateAudioSettings(conversationId, { voiceId, voiceName });
      // Dispatch event to notify ScriptAudioPanel instances to update their voice
      window.dispatchEvent(new CustomEvent('audio-settings-changed', {
        detail: { voiceId, voiceName, conversationId }
      }));
    } catch (err) {
      console.error('Failed to update voice setting:', err);
    }
  };

  // Handle model change
  const handleModelChange = async (model: 'flash' | 'pro') => {
    setSelectedModel(model);
    try {
      const ttsModel = model === 'pro' ? 'gemini-2.5-pro-tts' : 'gemini-2.5-flash-tts';
      await updateAudioSettings(conversationId, { ttsModel });
      // Dispatch event to notify ScriptAudioPanel instances to update their model
      window.dispatchEvent(new CustomEvent('audio-settings-changed', {
        detail: { ttsModel: model, conversationId }
      }));
    } catch (err) {
      console.error('Failed to update model setting:', err);
    }
  };

  // Generate all audio - uses same per-slide generation as ScriptAudioPanel
  const handleGenerateAllAudio = async () => {
    setIsGeneratingAudio(true);
    setError(null);

    // Clear any previous generating set
    setGeneratingSlideIds(new Set());

    // Debug: Log all scripts to see what we have
    console.log('🎧 [VideoScriptsPanel] All scripts:', scripts.map(s => ({
      slideId: s.slideId,
      slideIdentifier: s.slideIdentifier,
      hasScript: !!s.script?.scriptText,
      scriptPreview: s.script?.scriptText?.substring(0, 50),
    })));

    // Get slides that have scripts AND a valid slideId (database UUID)
    const slidesWithScripts = scripts.filter(s => s.script?.scriptText && s.slideId);

    console.log('🎧 [VideoScriptsPanel] Slides with scripts and valid slideId:', slidesWithScripts.length,
      slidesWithScripts.map(s => ({ slideId: s.slideId, slideIdentifier: s.slideIdentifier })));

    if (slidesWithScripts.length === 0) {
      toast.error('No slides with scripts to generate audio for');
      setIsGeneratingAudio(false);
      return;
    }

    // Pre-initialize audioStatus for all slides being generated
    // This ensures SSE events can find and update these slides
    const initialAudioStatus: SlideAudioStatusSummary[] = slidesWithScripts.map((slideScript, index) => ({
      slideId: slideScript.slideId,
      slideIdentifier: slideScript.slideIdentifier,
      slideNumber: index + 1,
      title: slideScript.title,
      hasAudio: false,
      audioStatus: 'PENDING' as const,
      audioUrl: null,
      audioDuration: null,
      version: null,
      scriptText: slideScript.script?.scriptText || null,
    }));

    console.log('🎧 [VideoScriptsPanel] Pre-initialized audioStatus with', initialAudioStatus.length, 'slides:',
      initialAudioStatus.map(s => ({ slideId: s.slideId, slideIdentifier: s.slideIdentifier }))
    );
    setAudioStatus(initialAudioStatus);

    // Mark all slides as generating to prevent UI flickering during status transitions
    const slideIdsBeingGenerated = new Set(slidesWithScripts.map(s => s.slideId));
    setGeneratingSlideIds(slideIdsBeingGenerated);
    console.log('🎧 [VideoScriptsPanel] Marked slides as generating:', Array.from(slideIdsBeingGenerated));

    try {
      // Get voice name for the request
      const voiceName = voices.find(v => v.id === selectedVoice)?.name || selectedVoice;
      const ttsModel = selectedModel === 'pro' ? 'pro' : 'flash';

      // Queue audio generation for each slide (same as ScriptAudioPanel)
      let queued = 0;
      for (const slideScript of slidesWithScripts) {
        try {
          console.log(`🎧 [VideoScriptsPanel] Queueing audio for ${slideScript.slideIdentifier} (slideId: ${slideScript.slideId})`);
          await generateAudio(slideScript.slideId, {
            voiceId: selectedVoice,
            voiceName,
            ttsModel,
          });
          queued++;
          console.log(`🎧 [VideoScriptsPanel] ✅ Queued ${slideScript.slideIdentifier}`);
        } catch (err) {
          console.error(`Failed to queue audio for slide ${slideScript.slideIdentifier}:`, err);
        }
      }

      if (queued > 0) {
        toast.info(`Queued audio generation for ${queued} slides...`);
        // Start polling for status updates
        audioPollingRef.current = setInterval(loadAudioStatus, 2000);
      } else {
        toast.error('Failed to queue any audio generation');
        setIsGeneratingAudio(false);
      }
    } catch (err) {
      console.error('Failed to generate all audio:', err);
      setError('Failed to start audio generation');
      setIsGeneratingAudio(false);
      toast.error('Failed to start audio generation');
    }
  };

  // Apply all audio to slides (with animation choreography if word timings exist)
  const handleApplyAllAudio = async () => {
    const slidesWithAudio = audioStatus.filter(s => s.audioStatus === 'COMPLETED');
    if (slidesWithAudio.length === 0) {
      toast.error('No slides have completed audio to apply');
      return;
    }

    toast.info(`Applying audio to ${slidesWithAudio.length} slides...`, { duration: 2000 });
    setIsApplyingAll(true);
    startApplyAll();

    // Get script text for each slide (needed for choreographer context)
    const slidesWithScripts = slidesWithAudio.map(slide => {
      const script = scripts.find(s =>
        s.slideId === slide.slideId || s.slideIdentifier === slide.slideIdentifier
      );
      return {
        ...slide,
        scriptText: script?.script?.scriptText || '',
      };
    });

    // Use shared function for each slide (triggers choreography if word timings exist)
    const results = await Promise.all(
      slidesWithScripts.map(slide =>
        applyAudioWithChoreography({
          slideId: slide.slideId,
          slideIdentifier: slide.slideIdentifier,
          conversationId,
          scriptText: slide.scriptText,
          registerJob: registerApplyJob,
        })
      )
    );

    const choreographyJobsCreated = results.filter(r => r.choreographyJobCreated).length;
    const successCount = results.filter(r => r.success).length;
    console.log(`[VideoScriptsPanel] Applied audio to ${successCount} slides, choreography jobs created: ${choreographyJobsCreated}`);

    // If no choreography jobs were needed, complete immediately
    if (choreographyJobsCreated === 0) {
      endApplyAll();
      setIsApplyingAll(false);
      toast.success(`Applied audio to ${successCount} slides!`);
    } else {
      // Choreography jobs are running - the hook will auto-end Apply All when the last job completes
      toast.info(`Syncing animations for ${choreographyJobsCreated} slides...`, { duration: 3000 });
    }

    await loadAudioStatus();
  };

  // Get audio status for a slide
  const getAudioStatusForSlide = (slideId: string): SlideAudioStatusSummary | undefined => {
    return audioStatus.find(s => s.slideIdentifier === slideId || s.slideId === slideId);
  };

  // Calculate audio summary
  const audioSummary = {
    completed: audioStatus.filter(s => s.audioStatus === 'COMPLETED').length,
    generating: audioStatus.filter(s => s.audioStatus === 'GENERATING' || s.audioStatus === 'ALIGNING').length,
    total: audioStatus.length,
  };

  // Check if any scripts exist
  const hasExistingScripts = scripts.some(s => s.script?.scriptText);
  const scriptsGenerated = scripts.filter(s => s.script?.scriptText).length;
  const totalSlides = outline.slides.length;

  // Handle generate button click
  const handleGenerateClick = () => {
    if (hasExistingScripts) {
      setShowRegenerateWarning(true);
    } else {
      startGeneration();
    }
  };

  // Start script generation
  const startGeneration = async (regenerate = false) => {
    setShowRegenerateWarning(false);
    setIsGenerating(true);
    setGenerationProgress(null);
    setError(null);

    try {
      // Start the agent job
      const result = await outlineAPI.generateScripts(conversationId, {
        regenerate,
        userInstructions: 'Professional and engaging tone',
      });

      if (!result.success || !result.jobId) {
        throw new Error('Failed to start script generation');
      }

      // Save jobId for persistence/reconnection
      setScriptJobId(result.jobId);
      sessionStorage.setItem(`script_generation_${conversationId}`, result.jobId);

      // Subscribe to job progress
      const unsubscribe = subscribeToAgentJob(result.jobId, {
        onProgress: (progress: AgentJobProgress) => {
          setGenerationProgress(progress);
        },
        onComplete: async () => {
          // Close SSE connection to prevent auto-reconnect
          scriptUnsubscribeRef.current?.();
          scriptUnsubscribeRef.current = null;
          // Clear persistence on completion
          sessionStorage.removeItem(`script_generation_${conversationId}`);
          setScriptJobId(null);
          setIsGenerating(false);
          setGenerationProgress(null);
          // Reload scripts after generation
          await loadScripts();
          // Notify ScriptAudioPanel to reload from DB
          window.dispatchEvent(new CustomEvent('scripts-batch-updated', {
            detail: { conversationId, timestamp: Date.now(), source: 'video_script_planner' }
          }));
          onScriptsGenerated?.();
        },
        onError: (errorMsg: string) => {
          console.error('Script generation error:', errorMsg);
          // Close SSE connection on error
          scriptUnsubscribeRef.current?.();
          scriptUnsubscribeRef.current = null;
          // Clear persistence on error
          sessionStorage.removeItem(`script_generation_${conversationId}`);
          setScriptJobId(null);
          setError(errorMsg || 'Script generation failed');
          setIsGenerating(false);
          setGenerationProgress(null);
          toast.error(`Script generation failed: ${errorMsg || 'Unknown error'}`);
        },
      });

      // Store unsubscribe function for cleanup
      scriptUnsubscribeRef.current = unsubscribe;
    } catch (err) {
      console.error('Failed to start script generation:', err);
      setError((err as Error).message || 'Failed to start script generation');
      setIsGenerating(false);
    }
  };

  // Get script for a slide (by outline slide ID like "slide-001")
  const getScriptForSlide = (outlineSlideId: string): SlideScript | undefined => {
    return scripts.find(s => s.slideIdentifier === outlineSlideId);
  };

  // Handle edit request from chat input
  const handleEditRequest = async (editInstructions: string) => {
    if (!editInstructions.trim()) return;

    setIsEditing(true);
    setEditProgress(null);
    setError(null);
    setEditInput('');

    try {
      const result = await outlineAPI.editScripts(conversationId, {
        editInstructions: editInstructions.trim(),
      });

      if (!result.success || !result.jobId) {
        throw new Error('Failed to start script edit');
      }

      // Save jobId for persistence/reconnection
      setEditJobId(result.jobId);
      sessionStorage.setItem(`script_edit_${conversationId}`, result.jobId);

      // Subscribe to job progress
      const unsubscribe = subscribeToAgentJob(result.jobId, {
        onProgress: (progress: AgentJobProgress) => {
          setEditProgress(progress);
        },
        onComplete: async () => {
          // Close SSE connection to prevent auto-reconnect
          editUnsubscribeRef.current?.();
          editUnsubscribeRef.current = null;
          // Clear persistence on completion
          sessionStorage.removeItem(`script_edit_${conversationId}`);
          setEditJobId(null);
          setIsEditing(false);
          setEditProgress(null);
          // Reload scripts after edit
          await loadScripts();
          // Notify ScriptAudioPanel to reload from DB
          window.dispatchEvent(new CustomEvent('scripts-batch-updated', {
            detail: { conversationId, timestamp: Date.now(), source: 'video_script_planner' }
          }));
          onScriptsGenerated?.();
        },
        onError: (errorMsg: string) => {
          console.error('Script edit error:', errorMsg);
          // Close SSE connection on error
          editUnsubscribeRef.current?.();
          editUnsubscribeRef.current = null;
          // Clear persistence on error
          sessionStorage.removeItem(`script_edit_${conversationId}`);
          setEditJobId(null);
          setError(errorMsg || 'Script edit failed');
          setIsEditing(false);
          setEditProgress(null);
          toast.error(`Script edit failed: ${errorMsg || 'Unknown error'}`);
        },
      });

      // Store unsubscribe function for cleanup
      editUnsubscribeRef.current = unsubscribe;
    } catch (err) {
      console.error('Failed to start script edit:', err);
      setError((err as Error).message || 'Failed to start script edit');
      setIsEditing(false);
    }
  };

  // Handle chat input submit
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editInput.trim() && !isEditing && !isGenerating) {
      handleEditRequest(editInput);
    }
  };

  // Inline editing handlers
  const handleStartInlineEdit = useCallback((slideId: string, currentText: string) => {
    setEditingSlideId(slideId);
    setEditText(currentText);
  }, []);

  const handleSaveInlineEdit = useCallback(async () => {
    if (!editingSlideId || isSavingEdit) return;

    setIsSavingEdit(true);
    try {
      // Call API to update script (uses audioService.updateScript)
      await updateScript(editingSlideId, editText);

      // Update local state
      setScripts(prev => prev.map(s =>
        s.slideId === editingSlideId
          ? { ...s, script: { ...s.script!, scriptText: editText, updatedAt: new Date().toISOString() } }
          : s
      ));

      // Dispatch event for cross-panel sync (ScriptAudioPanel listens for this)
      window.dispatchEvent(new CustomEvent('script-updated', {
        detail: {
          conversationId,
          slideId: editingSlideId,
          scriptText: editText,
          timestamp: new Date().toISOString(),
        },
      }));

      toast.success('Script saved');
      setEditingSlideId(null);
      setEditText('');
    } catch (err) {
      console.error('Failed to save script:', err);
      toast.error('Failed to save script');
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingSlideId, editText, isSavingEdit, conversationId]);

  const handleCancelInlineEdit = useCallback(() => {
    setEditingSlideId(null);
    setEditText('');
  }, []);

  // Audio playback handler - only one slide can play at a time
  const handlePlayChange = useCallback((slideId: string, playing: boolean) => {
    if (playing) {
      // Stop any currently playing audio first
      setCurrentlyPlayingSlideId(slideId);
    } else {
      // Only clear if this is the currently playing slide
      setCurrentlyPlayingSlideId(prev => prev === slideId ? null : prev);
    }
  }, []);

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Header */}
      <div className="flex-shrink-0 h-10 flex items-center justify-between px-3 border-b border-border/50">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold">Video Scripts</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
            {scriptsGenerated}/{totalSlides}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-0.5 p-0.5 bg-muted rounded-md">
            <button
              onClick={() => setViewMode('compact')}
              className={cn(
                "p-1 rounded transition-colors",
                viewMode === 'compact'
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
              title="Compact view"
            >
              <List className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('expanded')}
              className={cn(
                "p-1 rounded transition-colors",
                viewMode === 'expanded'
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
              title="Expanded view"
            >
              <AlignJustify className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Generate Button */}
          <Button
            size="sm"
            variant={hasExistingScripts ? 'outline' : 'default'}
            className="h-7 text-xs px-2.5"
            onClick={handleGenerateClick}
            disabled={isGenerating || outline.slides.length === 0}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Generating...
              </>
            ) : hasExistingScripts ? (
              <>
                <RefreshCw className="w-3 h-3 mr-1" />
                Regenerate
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 mr-1" />
                Generate All
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Generation Progress */}
      {isGenerating && (
        <div className="flex-shrink-0 p-3 border-b border-border/50">
          <AIThinkingCard
            thinking={generationProgress?.thinking || generationProgress?.currentOperation}
            isActive={isGenerating}
            onCancel={scriptJobId ? handleCancelGeneration : undefined}
          />
        </div>
      )}

      {/* Edit Progress */}
      {isEditing && (
        <div className="flex-shrink-0 p-3 border-b border-border/50">
          <AIThinkingCard
            thinking={editProgress?.thinking || editProgress?.currentOperation}
            isActive={isEditing}
            onCancel={editJobId ? handleCancelEdit : undefined}
            variant="blue"
          />
        </div>
      )}

      {/* Voice & Audio Settings - Always Visible */}
      {hasExistingScripts && (
        <div className="flex-shrink-0 border-b border-border/50 px-3 py-3 space-y-3 bg-muted/10">
          {/* Voice and Model Selection */}
          <div className="flex items-center gap-3">
            {/* Voice Selector - Same component as ScriptAudioPanel with play preview */}
            <VoiceSelector
              voices={voices}
              selectedVoice={selectedVoice}
              onVoiceChange={handleVoiceChange}
              disabled={isGeneratingAudio}
              className="flex-1"
            />

            {/* Model Toggle */}
            <div className="flex items-center gap-0.5 p-0.5 bg-muted rounded-md">
              <button
                onClick={() => handleModelChange('flash')}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-medium rounded transition-colors",
                  selectedModel === 'flash'
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Flash
              </button>
              <button
                onClick={() => handleModelChange('pro')}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-medium rounded transition-colors",
                  selectedModel === 'pro'
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Pro
              </button>
            </div>
          </div>

          {/* Generate All Audio Button */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="flex-1 h-8 text-xs"
              onClick={handleGenerateAllAudio}
              disabled={isGeneratingAudio || scriptsGenerated === 0}
            >
              {isGeneratingAudio ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  Generating... ({audioSummary.completed}/{audioSummary.total})
                </>
              ) : audioSummary.completed === audioSummary.total && audioSummary.total > 0 ? (
                <>
                  <CheckCircle2 className="w-3 h-3 mr-1.5" />
                  All Audio Ready
                </>
              ) : audioSummary.completed > 0 ? (
                <>
                  <Play className="w-3 h-3 mr-1.5" />
                  Generate Remaining ({audioSummary.total - audioSummary.completed})
                </>
              ) : (
                <>
                  <Play className="w-3 h-3 mr-1.5" />
                  Generate All Audio
                </>
              )}
            </Button>

            {/* Apply All Button - show when audio is ready */}
            {audioSummary.completed > 0 && !isGeneratingAudio && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={handleApplyAllAudio}
                disabled={isApplyingAll}
              >
                {isApplyingAll ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Apply All
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Audio Generation Progress */}
          {isGeneratingAudio && audioSummary.generating > 0 && (
            <div className="space-y-1.5">
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(audioSummary.completed / Math.max(audioSummary.total, 1)) * 100}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground text-center">
                {audioSummary.generating} generating, {audioSummary.completed} completed
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex-shrink-0 p-3 bg-destructive/10 border-b border-destructive/20">
          <div className="flex items-center gap-2 text-xs text-destructive">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Scripts List */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : outline.slides.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <FileText className="w-8 h-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No slides in outline</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Add slides to your outline first
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {outline.slides.filter(s => !s.isDeleted).map((slide, index) => {
              const slideScript = getScriptForSlide(slide.id);
              const isThisEditing = slideScript?.slideId ? editingSlideId === slideScript.slideId : false;
              return (
                <ScriptCard
                  key={slide.id}
                  slide={slide}
                  script={slideScript}
                  slideIndex={index}
                  audioStatus={getAudioStatusForSlide(slide.id)}
                  isForceGenerating={slideScript?.slideId ? generatingSlideIds.has(slideScript.slideId) : false}
                  isExpanded={viewMode === 'expanded'}
                  isEditing={isThisEditing}
                  editText={isThisEditing ? editText : ''}
                  onEditTextChange={setEditText}
                  onStartEdit={() => slideScript?.slideId && handleStartInlineEdit(slideScript.slideId, slideScript.script?.scriptText || '')}
                  onSaveEdit={handleSaveInlineEdit}
                  onCancelEdit={handleCancelInlineEdit}
                  isSaving={isSavingEdit}
                  currentlyPlayingSlideId={currentlyPlayingSlideId}
                  onPlayChange={handlePlayChange}
                />
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Draft Scripts Warning Banner - Show when scripts exist but no unified narrative */}
      {hasExistingScripts && !hasNarrativeContext && !isGenerating && (
        <div className="flex-shrink-0 border-t border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-amber-800 dark:text-amber-400">
                Draft scripts from slide generation
              </p>
              <p className="text-[11px] text-amber-700 dark:text-amber-500">
                Click "Generate All" above to create a unified narrative with consistent voice and enable AI editing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Script Edit Chat Input - Only enabled after video-level generation */}
      {hasExistingScripts && (
        <div className="flex-shrink-0 border-t border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Sparkles className="w-3 h-3" />
            <span>Edit scripts with AI</span>
            {!hasNarrativeContext && (
              <span className="text-amber-600 dark:text-amber-500 ml-1">(run "Generate All" first)</span>
            )}
          </div>
          <form onSubmit={handleEditSubmit} className="flex gap-2">
            <Input
              value={editInput}
              onChange={(e) => setEditInput(e.target.value)}
              placeholder={hasNarrativeContext
                ? "e.g., 'Make slide 3 more enthusiastic' or 'Shorten all scripts'"
                : "Generate unified scripts first to enable AI editing"
              }
              className="h-9 text-xs bg-background"
              disabled={isEditing || isGenerating || !hasNarrativeContext}
            />
            <Button
              type="submit"
              size="sm"
              className="h-9 px-3"
              disabled={!editInput.trim() || isEditing || isGenerating || !hasNarrativeContext}
              title={!hasNarrativeContext ? 'Run "Generate All" first to enable AI editing' : undefined}
            >
              {isEditing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        </div>
      )}

      {/* Regeneration Warning Dialog */}
      <Dialog open={showRegenerateWarning} onOpenChange={setShowRegenerateWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate All Scripts?</DialogTitle>
            <DialogDescription>
              This will overwrite all existing scripts. Your manual edits will be lost.
              Audio files will remain until you regenerate them individually.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateWarning(false)}>
              Cancel
            </Button>
            <Button onClick={() => startGeneration(true)}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoScriptsPanel;
